import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ConfirmModal from '../components/Shared/ConfirmModal';

const AgencyCard = ({ agency, onEdit, onDelete }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="bg-slate-900 p-6 sm:p-8 rounded-3xl border border-slate-800 hover:border-teal-500/30 transition-all group relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 blur-[60px] -mr-16 -mt-16"></div>
      
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-xl font-black shadow-lg" style={{ backgroundColor: `${agency.color}20`, color: agency.color, border: `1px solid ${agency.color}40` }}>
            <SafeIcon icon={FiIcons.FiBriefcase} />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-white truncate uppercase">{agency.name}</h3>
            <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Agency Partner</p>
          </div>
        </div>
        <button 
          onClick={() => onEdit(agency)}
          className="p-2 text-slate-600 hover:text-teal-400 transition-colors shrink-0"
          title="Edit Agency"
        >
          <SafeIcon icon={FiIcons.FiEdit3} />
        </button>
      </div>
      
      <div className="space-y-4 font-bold">
        <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 flex justify-between items-center">
          <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Portal Access</span>
          <Link 
            to={`/portal/${agency.id}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-teal-400 text-sm font-black hover:underline flex items-center gap-1"
          >
            View <SafeIcon icon={FiIcons.FiExternalLink} className="text-xs" />
          </Link>
        </div>
        
        <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex justify-between items-center">
          <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Password</span>
          <div className="flex items-center gap-2">
            <code className="text-teal-500 font-mono text-sm leading-none">
              {showPassword ? agency.password : '••••••••'}
            </code>
            <button 
              onClick={() => setShowPassword(!showPassword)}
              className="text-slate-600 hover:text-teal-400 transition-colors"
            >
              <SafeIcon icon={showPassword ? FiIcons.FiEyeOff : FiIcons.FiEye} className="text-sm" />
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button 
              onClick={() => onDelete(agency.id)} 
              className="text-[10px] font-black text-red-500/40 hover:text-red-500 uppercase tracking-widest transition-colors"
          >
              Delete Agency
          </button>
        </div>
      </div>
    </div>
  );
};

const Agencies = () => {
  const { agencies, addAgency, updateAgency, removeAgency, isLoading } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  
  const [formData, setFormData] = useState({ 
    name: '', 
    color: '#14b8a6', 
    password: '' 
  });

  const handleOpenModal = (agency = null) => {
    if (agency) {
      setEditingAgency(agency);
      setFormData({ 
        name: agency.name || '', 
        color: agency.color || '#14b8a6', 
        password: agency.password || '' 
      });
    } else {
      setEditingAgency(null);
      setFormData({ name: '', color: '#14b8a6', password: '' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAgency) {
        await updateAgency(editingAgency.id, {
          name: formData.name,
          color: formData.color,
          password: formData.password
        });
      } else {
        await addAgency(formData);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to save agency:", err);
    }
  };

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 sm:gap-4">
        <div>
          <p className="text-teal-500 font-bold text-[10px] uppercase tracking-widest mb-1">Partnership Management</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white">Agency Partners</h1>
        </div>
        <button 
          onClick={() => handleOpenModal()} 
          className="w-full sm:w-auto bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-all font-black shadow-lg shadow-teal-900/40 text-sm uppercase"
        >
          <SafeIcon icon={FiIcons.FiPlus} /> Add Agency
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {agencies.map(agency => (
          <AgencyCard 
            key={agency.id} 
            agency={agency} 
            onEdit={handleOpenModal} 
            onDelete={(id) => setDeleteConfirm({ show: true, id })}
          />
        ))}
        {agencies.length === 0 && !isLoading && (
          <div className="col-span-full p-20 text-center bg-slate-900/50 rounded-3xl border border-slate-800 border-dashed">
            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest">No agency partners registered</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200">
            <h2 className="text-2xl font-black text-white mb-8">{editingAgency ? 'Edit Agency' : 'Register Agency'}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Agency Name</label>
                <input 
                  autoFocus 
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm font-bold" 
                  value={formData.name} 
                  onChange={e => setFormData({...formData, name: e.target.value})} 
                  placeholder="e.g. Skyline Digital"
                  required 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Brand Color</label>
                  <input 
                    type="color" 
                    className="w-full h-12 bg-slate-800 border border-slate-700 rounded-xl px-1 py-1 cursor-pointer" 
                    value={formData.color} 
                    onChange={e => setFormData({...formData, color: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Password</label>
                  <input 
                    type="text" 
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm font-bold" 
                    value={formData.password} 
                    onChange={e => setFormData({...formData, password: e.target.value})} 
                    placeholder="Portal pass" 
                    required 
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2 text-slate-500 hover:text-white font-bold transition-colors text-sm uppercase tracking-widest">Cancel</button>
                <button 
                  type="submit" 
                  className="px-8 py-3 bg-teal-600 text-white rounded-xl font-black hover:bg-teal-500 transition-all text-sm uppercase shadow-lg shadow-teal-900/40"
                >
                  {editingAgency ? 'Save Changes' : 'Create Agency'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal 
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, id: null })}
        onConfirm={() => removeAgency(deleteConfirm.id)}
        title="Remove Agency?"
        message="This will permanently delete the agency partner and unlink their assigned clients from the partner portal."
      />
    </div>
  );
};

export default Agencies;